
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Filter, FileSpreadsheet, FileText, FileArchive } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface CRMActionsProps {
  onToggleFilter: () => void;
  onDownload: () => void;
  onExportExcel: () => void;
  showFilters: boolean;
  filteredMembershipIds?: string[];
}

export const CRMActions = ({ onToggleFilter, onDownload, onExportExcel, showFilters, filteredMembershipIds }: CRMActionsProps) => {
  const [isDownloadingCerfa, setIsDownloadingCerfa] = useState(false);

  const handleDownloadAllCerfa = async () => {
    try {
      setIsDownloadingCerfa(true);
      toast({
        title: "Téléchargement en cours...",
        description: "Préparation de l'archive ZIP de tous les CERFAs. Cela peut prendre un moment.",
      });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Erreur", description: "Vous devez être connecté.", variant: "destructive" });
        return;
      }

      const fetchOptions: RequestInit = {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ membershipIds: filteredMembershipIds || [] }),
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-all-cerfa`,
        fetchOptions
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Erreur lors du téléchargement");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CERFA_tous_adherents_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Téléchargement terminé",
        description: "L'archive ZIP des CERFAs a été téléchargée.",
      });
    } catch (err: any) {
      console.error("Erreur téléchargement CERFAs:", err);
      toast({
        title: "Erreur",
        description: err.message || "Impossible de télécharger les CERFAs.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingCerfa(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Button onClick={onToggleFilter} variant="outline" className="flex items-center gap-2">
        <Filter size={16} />
        Filtres {showFilters ? '▲' : '▼'}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2">
            <Download size={16} />
            Exporter
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem onClick={onDownload} className="flex items-center gap-2">
            <FileSpreadsheet size={16} />
            <span>CSV</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onExportExcel} className="flex items-center gap-2">
            <FileText size={16} />
            <span>Excel</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={handleDownloadAllCerfa} 
            disabled={isDownloadingCerfa}
            className="flex items-center gap-2"
          >
            <FileArchive size={16} />
            <span>{isDownloadingCerfa ? "Téléchargement..." : "CERFAs filtrés (ZIP)"}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
